<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { toast } from 'dashboard/composables/useToast';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

// Uma tela, dois usos: o admin ve tudo da conta; o agente (rota "mine") ve
// so o que ele mesmo agendou. A diferenca e o fetch e a coluna "Agente".
const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const isMine = computed(() => Boolean(route.meta?.mine));

const scheduledMessages = useMapGetter(
  'scheduledMessages/getScheduledMessages'
);
const uiFlags = useMapGetter('scheduledMessages/getUIFlags');

const statusFilter = ref('');
const editDialogRef = ref(null);
const editingMessage = ref(null);
const editContent = ref('');
const editDate = ref('');
const editTime = ref('');
const isUpdating = ref(false);

const statusConfig = {
  pending: {
    label: 'SCHEDULED_MESSAGES.STATUS.PENDING',
    class: 'bg-n-amber-3 text-n-amber-11',
    icon: 'i-lucide-clock',
  },
  sent: {
    label: 'SCHEDULED_MESSAGES.STATUS.SENT',
    class: 'bg-n-teal-3 text-n-teal-11',
    icon: 'i-lucide-check',
  },
  failed: {
    label: 'SCHEDULED_MESSAGES.STATUS.FAILED',
    class: 'bg-n-ruby-3 text-n-ruby-11',
    icon: 'i-lucide-x',
  },
  cancelled: {
    label: 'SCHEDULED_MESSAGES.STATUS.CANCELLED',
    class: 'bg-n-slate-3 text-n-slate-10',
    icon: 'i-lucide-ban',
  },
};

// Entrega de verdade, lida da mensagem que nasceu na conversa.
const deliveryConfig = {
  read: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.READ',
    class: 'text-n-teal-11',
    icon: 'i-lucide-check-check',
  },
  delivered: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.DELIVERED',
    class: 'text-n-slate-11',
    icon: 'i-lucide-check-check',
  },
  sent: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.SENT',
    class: 'text-n-slate-11',
    icon: 'i-lucide-check',
  },
  delivering: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.DELIVERING',
    class: 'text-n-slate-10',
    icon: 'i-lucide-clock',
  },
  failed: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.FAILED',
    class: 'text-n-ruby-11',
    icon: 'i-lucide-triangle-alert',
  },
  unknown: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.UNKNOWN',
    class: 'text-n-slate-10',
    icon: 'i-lucide-help-circle',
  },
};

const getStatusConfig = status => statusConfig[status] || statusConfig.pending;
const getDeliveryConfig = msg =>
  deliveryConfig[msg.message_status] || deliveryConfig.unknown;

// Pendentes primeiro (a proxima a sair no topo); o resto, mais recente antes.
const filteredMessages = computed(() => {
  const messages = [...(scheduledMessages.value || [])];
  const list = statusFilter.value
    ? messages.filter(m => m.status === statusFilter.value)
    : messages;
  return list.sort((a, b) => {
    const ap = a.status === 'pending' ? 0 : 1;
    const bp = b.status === 'pending' ? 0 : 1;
    if (ap !== bp) return ap - bp;
    const at = new Date(a.scheduled_at) - new Date(b.scheduled_at);
    return ap === 0 ? at : -at;
  });
});

const tableHeaders = computed(() => {
  const headers = [
    t('SCHEDULED_MESSAGES.ADMIN.TABLE.CONTACT'),
    t('SCHEDULED_MESSAGES.ADMIN.TABLE.CONTENT'),
    t('SCHEDULED_MESSAGES.ADMIN.TABLE.SCHEDULED_AT'),
    t('SCHEDULED_MESSAGES.ADMIN.TABLE.STATUS'),
    t('SCHEDULED_MESSAGES.ADMIN.TABLE.SENT_AT'),
  ];
  if (!isMine.value) headers.push(t('SCHEDULED_MESSAGES.ADMIN.TABLE.AGENT'));
  headers.push(t('SCHEDULED_MESSAGES.ADMIN.TABLE.ACTIONS'));
  return headers;
});

const formatDateTime = dateStr => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const today = computed(() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
});

const editScheduledDateTime = computed(() => {
  if (!editDate.value || !editTime.value) return null;
  return new Date(`${editDate.value}T${editTime.value}`);
});

const isEditFormValid = computed(() => {
  if (!editContent.value.trim()) return false;
  if (!editScheduledDateTime.value) return false;
  if (editScheduledDateTime.value <= new Date()) return false;
  return true;
});

const conversationLine = msg =>
  `#${msg.display_id || msg.conversation_id} · ${msg.inbox_name || '-'}`;

// A rota da conversa usa o numero de exibicao (#1468), nao o id interno.
const openConversation = msg => {
  const displayId = msg.display_id || msg.conversation_id;
  if (!displayId) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: displayId,
    },
  });
};

const openEditDialog = msg => {
  editingMessage.value = msg;
  editContent.value = msg.content || '';
  const dt = new Date(msg.scheduled_at);
  editDate.value = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  editTime.value = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
  editDialogRef.value?.open();
};

const closeEditDialog = () => {
  editingMessage.value = null;
  editContent.value = '';
  editDate.value = '';
  editTime.value = '';
  editDialogRef.value?.close();
};

const saveEdit = async () => {
  if (!editingMessage.value || !isEditFormValid.value) return;
  isUpdating.value = true;
  try {
    await store.dispatch('scheduledMessages/update', {
      id: editingMessage.value.id,
      scheduled_message: {
        content: editContent.value.trim(),
        scheduled_at: editScheduledDateTime.value.toISOString(),
        conversation_id: editingMessage.value.conversation_id,
        inbox_id: editingMessage.value.inbox_id,
      },
    });
    toast.success(t('SCHEDULED_MESSAGES.ALERTS.UPDATED'));
    closeEditDialog();
  } catch {
    toast.error(t('SCHEDULED_MESSAGES.ALERTS.UPDATE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const cancelMessage = async message => {
  try {
    await store.dispatch('scheduledMessages/cancel', message.id);
    toast.success(t('SCHEDULED_MESSAGES.ALERTS.CANCELLED'));
  } catch {
    toast.error(t('SCHEDULED_MESSAGES.ALERTS.CANCEL_ERROR'));
  }
};

const deleteMessage = async message => {
  try {
    await store.dispatch('scheduledMessages/delete', message.id);
    toast.success(t('SCHEDULED_MESSAGES.ALERTS.DELETED'));
  } catch {
    toast.error(t('SCHEDULED_MESSAGES.ALERTS.DELETE_ERROR'));
  }
};

const filterStatuses = [
  { key: '', label: 'SCHEDULED_MESSAGES.ADMIN.FILTER.ALL' },
  { key: 'pending', label: 'SCHEDULED_MESSAGES.ADMIN.FILTER.PENDING' },
  { key: 'sent', label: 'SCHEDULED_MESSAGES.ADMIN.FILTER.SENT' },
  { key: 'failed', label: 'SCHEDULED_MESSAGES.ADMIN.FILTER.FAILED' },
  { key: 'cancelled', label: 'SCHEDULED_MESSAGES.ADMIN.FILTER.CANCELLED' },
];

const fetchMessages = () => {
  store.dispatch(
    isMine.value ? 'scheduledMessages/getMine' : 'scheduledMessages/get'
  );
};

// O mesmo componente serve as duas rotas; trocar entre elas precisa refazer o fetch.
watch(() => route.name, fetchMessages);
onMounted(fetchMessages);
</script>

<template>
  <SettingsPageLayout
    :title="
      isMine
        ? $t('SCHEDULED_MESSAGES.MINE.TITLE')
        : $t('SCHEDULED_MESSAGES.ADMIN.TITLE')
    "
    :description="
      isMine
        ? $t('SCHEDULED_MESSAGES.MINE.DESCRIPTION')
        : $t('SCHEDULED_MESSAGES.ADMIN.DESCRIPTION')
    "
  >
    <template #actions>
      <div class="flex items-center gap-1">
        <Button
          v-for="filter in filterStatuses"
          :key="filter.key"
          xs
          :color="statusFilter === filter.key ? 'blue' : 'slate'"
          :variant="statusFilter === filter.key ? 'solid' : 'faded'"
          :label="$t(filter.label)"
          @click="statusFilter = filter.key"
        />
      </div>
    </template>

    <SettingsTable
      :headers="tableHeaders"
      :is-loading="uiFlags.isFetching"
      :is-empty="!filteredMessages.length"
      :empty-message="
        isMine
          ? $t('SCHEDULED_MESSAGES.MINE.EMPTY')
          : $t('SCHEDULED_MESSAGES.ADMIN.EMPTY')
      "
      dense
    >
      <tr
        v-for="msg in filteredMessages"
        :key="msg.id"
        class="transition-colors hover:bg-n-solid-3"
      >
        <td class="px-3 py-2 max-w-[200px]">
          <button
            class="block w-full text-left"
            :title="$t('SCHEDULED_MESSAGES.ADMIN.OPEN_CONVERSATION')"
            @click="openConversation(msg)"
          >
            <span
              class="block text-sm text-n-slate-12 truncate hover:underline"
            >
              {{ msg.contact_name || '-' }}
            </span>
            <span class="block text-xs text-n-slate-10 truncate">
              {{ conversationLine(msg) }}
            </span>
          </button>
        </td>
        <td class="px-3 py-2 max-w-[280px]">
          <span
            class="block text-sm text-n-slate-12 truncate"
            :title="msg.content"
          >
            {{ msg.content }}
          </span>
        </td>
        <td class="px-3 py-2 whitespace-nowrap">
          <span class="text-sm text-n-slate-11 tabular-nums">
            {{ formatDateTime(msg.scheduled_at) }}
          </span>
        </td>
        <td class="px-3 py-2 whitespace-nowrap">
          <div class="flex flex-col items-start gap-0.5">
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md"
              :class="getStatusConfig(msg.status).class"
              :title="msg.error || ''"
            >
              <span :class="getStatusConfig(msg.status).icon" class="size-3" />
              {{ $t(getStatusConfig(msg.status).label) }}
            </span>
            <span
              v-if="msg.status === 'sent'"
              class="inline-flex items-center gap-1 text-[11px]"
              :class="getDeliveryConfig(msg).class"
            >
              <span :class="getDeliveryConfig(msg).icon" class="size-3" />
              {{ $t(getDeliveryConfig(msg).label) }}
            </span>
            <span
              v-else-if="msg.status === 'failed' && msg.error"
              class="max-w-[220px] truncate text-[11px] text-n-ruby-11"
              :title="msg.error"
            >
              {{ msg.error }}
            </span>
          </div>
        </td>
        <td class="px-3 py-2 whitespace-nowrap">
          <span class="text-sm text-n-slate-11 tabular-nums">
            {{ formatDateTime(msg.sent_at || msg.failed_at) || '-' }}
          </span>
        </td>
        <td v-if="!isMine" class="px-3 py-2 max-w-[140px]">
          <span class="block text-sm text-n-slate-11 truncate">
            {{ msg.user?.name || '-' }}
          </span>
        </td>
        <td class="px-3 py-2">
          <div class="flex items-center gap-1">
            <Button
              xs
              slate
              ghost
              icon="i-lucide-message-square"
              :title="$t('SCHEDULED_MESSAGES.ADMIN.OPEN_CONVERSATION')"
              @click="openConversation(msg)"
            />
            <Button
              v-if="msg.status === 'pending'"
              xs
              slate
              faded
              icon="i-lucide-pencil"
              :title="$t('SCHEDULED_MESSAGES.ACTIONS.EDIT')"
              @click="openEditDialog(msg)"
            />
            <Button
              v-if="msg.status === 'pending'"
              xs
              ruby
              faded
              icon="i-lucide-ban"
              :title="$t('SCHEDULED_MESSAGES.ACTIONS.CANCEL')"
              @click="cancelMessage(msg)"
            />
            <Button
              v-if="msg.status === 'cancelled' || msg.status === 'failed'"
              xs
              slate
              faded
              icon="i-lucide-trash-2"
              :title="$t('SCHEDULED_MESSAGES.ACTIONS.DELETE')"
              @click="deleteMessage(msg)"
            />
          </div>
        </td>
      </tr>
    </SettingsTable>
  </SettingsPageLayout>

  <!-- Edit Dialog -->
  <Dialog
    ref="editDialogRef"
    :title="$t('SCHEDULED_MESSAGES.EDIT_DIALOG.TITLE')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="closeEditDialog"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('SCHEDULED_MESSAGES.DIALOG.MESSAGE_LABEL') }}
        </label>
        <textarea
          v-model="editContent"
          class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand resize-none"
          rows="4"
          :placeholder="$t('SCHEDULED_MESSAGES.DIALOG.MESSAGE_PLACEHOLDER')"
        />
      </div>

      <div class="flex gap-3">
        <div class="flex flex-col flex-1 gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('SCHEDULED_MESSAGES.DIALOG.DATE_LABEL') }}
          </label>
          <input
            v-model="editDate"
            type="date"
            :min="today"
            class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand"
          />
        </div>
        <div class="flex flex-col flex-1 gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('SCHEDULED_MESSAGES.DIALOG.TIME_LABEL') }}
          </label>
          <input
            v-model="editTime"
            type="time"
            class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand"
          />
        </div>
      </div>

      <div class="flex items-center justify-end gap-2">
        <Button
          slate
          faded
          :label="$t('SCHEDULED_MESSAGES.ACTIONS.CANCEL')"
          @click="closeEditDialog"
        />
        <Button
          blue
          icon="i-lucide-check"
          :label="$t('SCHEDULED_MESSAGES.ACTIONS.UPDATE')"
          :disabled="!isEditFormValid || isUpdating"
          :is-loading="isUpdating"
          @click="saveEdit"
        />
      </div>
    </div>
  </Dialog>
</template>
